import { format } from 'date-fns';
import React, { useMemo } from 'react';

import { useAdminAiUsageStores } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlState from '@dashboard/hooks/useUrlState';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { Badge } from '@dashboard/components/ui/badge';

import { AI_USAGE_PLAN_LABELS, aiUsageStoreHref } from './aiUsageLabels';
import { type AiUsagePeriod, type AiUsageRange, formatMicros } from './aiUsagePeriod';

export type AiUsageStoreRow = {
  sellerId: string;
  name: string;
  displayName?: string;
  plan?: string;
  costMicros: number;
  turns: number;
  users: number;
  offTopicTurns: number;
  lastUsedAt: string;
  monthCostMicros: number;
  budgetMicros?: number;
};

type Props = { filters: AiUsageRange; period?: AiUsagePeriod };

const SORT_KEYS = ['costMicros', 'turns', 'lastUsedAt'];

const AiUsageStoresTable = ({ filters, period }: Props) => {
  const [query, setQuery] = useUrlState() as [
    Record<string, unknown>,
    (updates: Record<string, unknown>) => void,
  ];

  const sortBy =
    typeof query.sortBy === 'string' && SORT_KEYS.includes(query.sortBy)
      ? query.sortBy
      : 'costMicros';
  const sortDirection = Number(query.sortDirection) === 1 ? 1 : -1;
  const plan =
    typeof query.plan === 'string' && AI_USAGE_PLAN_LABELS[query.plan] ? query.plan : undefined;
  const { search, searchInput, setSearchInput } = useSearchInput(
    query.search == null ? '' : String(query.search),
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useAdminAiUsageStores(
    {
      from: filters.from,
      to: filters.to,
      plan,
      search,
      sortBy,
      sortDirection,
    },
  );

  const sorting = useMemo(
    () => [{ id: sortBy, desc: sortDirection === -1 }],
    [sortBy, sortDirection],
  );

  const flatData: AiUsageStoreRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const total: number | undefined = data?.pages[0]?.total;

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setQuery({ sortBy: id, sortDirection: desc ? -1 : 1 });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setQuery({ search: value.trim() });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }: any) => row.original.displayName || row.original.name,
        enableSorting: false,
      },
      {
        accessorKey: 'plan',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Plan" />,
        cell: ({ row }: any) => {
          const { plan: rowPlan } = row.original as AiUsageStoreRow;
          if (!rowPlan) return <span className="text-zinc-400 dark:text-muted-foreground">-</span>;
          return <Badge variant="outline">{AI_USAGE_PLAN_LABELS[rowPlan] ?? rowPlan}</Badge>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'costMicros',
        header: ({ column }: any) => (
          <DataGridColumnHeader column={column} title="Estimated spend" />
        ),
        cell: ({ row }: any) => formatMicros(row.original.costMicros),
        enableSorting: true,
      },
      {
        accessorKey: 'monthCostMicros',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="This month" />,
        cell: ({ row }: any) => {
          const { monthCostMicros, budgetMicros } = row.original as AiUsageStoreRow;
          if (budgetMicros == null) return formatMicros(monthCostMicros);
          return `${formatMicros(monthCostMicros)} of ${formatMicros(budgetMicros)}`;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'turns',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Turns" />,
        cell: ({ row }: any) => row.original.turns.toLocaleString(),
        enableSorting: true,
      },
      {
        accessorKey: 'users',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Users" />,
        cell: ({ row }: any) => row.original.users.toLocaleString(),
        enableSorting: false,
      },
      {
        accessorKey: 'offTopicTurns',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Off-topic" />,
        cell: ({ row }: any) => row.original.offTopicTurns.toLocaleString(),
        enableSorting: false,
      },
      {
        accessorKey: 'lastUsedAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Last used" />,
        cell: ({ row }: any) => format(new Date(row.original.lastUsedAt), 'd MMM yyyy, HH:mm'),
        enableSorting: true,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-x-2">
        <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
          {total !== undefined && `${total} ${total === 1 ? 'store' : 'stores'}`}
        </div>

        <div>
          <Label htmlFor="plan">Plan</Label>
          <Select
            name="plan"
            value={plan ?? 'all'}
            onValueChange={value => setQuery({ plan: value === 'all' ? undefined : value })}
          >
            <SelectTrigger id="plan" className="!w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              {Object.entries(AI_USAGE_PLAN_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="search">Search</Label>
          <div className="w-64">
            <div className="relative flex items-center">
              <TextInput
                border
                id="search"
                name="search"
                onChange={(e: any) => handleSearchChange(e.target.value)}
                placeholder="Search by store"
                value={searchInput}
              />
              {searchInput && (
                <button
                  aria-label="Clear search"
                  onClick={() => handleSearchChange('')}
                  className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-foreground transition-colors rounded"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        getRowId={row => row.sellerId}
        getRowHref={row => aiUsageStoreHref(row.sellerId, period)}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No AI usage in this period</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default AiUsageStoresTable;
